"use client"

import type { MembersBoardAgeMix, MembersBoardGenderMix } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty } from "@/features/dashboard/home-d4/components/home-tile"
import { useTranslations } from "@/i18n"

type Part = { key: string; label: string; value: number; swatch: string }

/**
 * Who we are: the gender split as one labelled bar, and the age bands as short bars against the largest band.
 * Side by side when the tile is wide, stacked when it is one track. Unknowns are counted, never hidden.
 */
export function PeopleMixTile({
  gender,
  age,
  subtitle,
}: {
  gender: MembersBoardGenderMix
  age: MembersBoardAgeMix
  subtitle: string
}) {
  const t = useTranslations("people.members.overview.board.mix")
  const total = gender.male + gender.female + gender.unknown

  const genderParts: Part[] = [
    { key: "female", label: t("female"), value: gender.female, swatch: "bg-violet-600 dark:bg-violet-400" },
    { key: "male", label: t("male"), value: gender.male, swatch: "bg-emerald-600 dark:bg-emerald-500" },
    ...(gender.unknown > 0
      ? [{ key: "unknown", label: t("notSet"), value: gender.unknown, swatch: "bg-slate-300 dark:bg-slate-600" }]
      : []),
  ]
  const bands = [
    { key: "under18", label: t("under18"), value: age.under18 },
    { key: "age18to35", label: t("age18to35"), value: age.age18to35 },
    { key: "age36to60", label: t("age36to60"), value: age.age36to60 },
    { key: "ageOver60", label: t("ageOver60"), value: age.ageOver60 },
  ]
  const largestBand = Math.max(...bands.map((band) => band.value))
  const topBand = bands.find((band) => band.value === largestBand && band.value > 0)

  return (
    <HomeTile title={t("title")} subtitle={subtitle} testId='members-board-mix'>
      {total === 0 ? (
        <HomeTileEmpty message={t("empty")} />
      ) : (
        <div className='@container flex min-h-0 flex-1 flex-col gap-3'>
          <div className='flex w-full flex-col gap-3 @lg:flex-row @lg:gap-8'>
            <section className='flex flex-col gap-1.5 @lg:flex-1'>
              <h3 className='text-[12px] font-bold text-muted-foreground'>{t("gender")}</h3>
              <div className='flex h-2.5 gap-0.5' aria-hidden>
                {genderParts
                  .filter((part) => part.value > 0)
                  .map((part) => (
                    <span key={part.key} className={`${part.swatch} rounded-[4px]`} style={{ width: `${(part.value / total) * 100}%` }} />
                  ))}
              </div>
              <dl className='flex gap-4'>
                {genderParts.map((part) => (
                  <div key={part.key} className='flex flex-col gap-px'>
                    <dt className='flex items-center gap-1.5 text-[11.5px] text-muted-foreground'>
                      <span aria-hidden className={`size-2 rounded-[2px] ${part.swatch}`} />
                      {part.label}
                    </dt>
                    <dd className='text-sm font-bold tabular-nums'>
                      {part.value}
                      <span className='ml-1 text-[11px] font-normal text-muted-foreground'>{Math.round((part.value / total) * 100)}%</span>
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
            <section className='flex flex-col gap-1.5 @lg:flex-1'>
              <h3 className='text-[12px] font-bold text-muted-foreground'>{t("age")}</h3>
              <dl className='flex flex-col gap-1.5 @lg:gap-2.5'>
                {bands.map((band) => (
                  <div key={band.key} data-testid='age-band' className='grid grid-cols-[6.5rem_1fr_2rem] items-center gap-2 text-[12px]'>
                    <dt className='truncate text-muted-foreground'>{band.label}</dt>
                    <span className='flex h-1.5 overflow-hidden rounded-full bg-muted' aria-hidden>
                      {band.value > 0 ? (
                        <span
                          className='rounded-full bg-teal-600 dark:bg-teal-500'
                          style={{ width: `${(band.value / largestBand) * 100}%` }}
                        />
                      ) : null}
                    </span>
                    <dd className='text-right font-bold tabular-nums'>{band.value}</dd>
                  </div>
                ))}
              </dl>
              {age.unknown > 0 ? (
                <span className='text-[11.5px] text-muted-foreground'>
                  {t(age.unknown === 1 ? "noBirthDateOne" : "noBirthDateOther", { count: age.unknown })}
                </span>
              ) : null}
            </section>
          </div>
          {topBand ? (
            <span className='mt-auto hidden text-[11.5px] text-muted-foreground @lg:block'>
              {t("largestBand", { band: topBand.label.toLocaleLowerCase(), count: topBand.value, share: Math.round((topBand.value / total) * 100) })}
            </span>
          ) : null}
        </div>
      )}
    </HomeTile>
  )
}
